import React from 'react'
import 'swiper/css'
import 'swiper/css/pagination'
import { A11y, Keyboard, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { getBlogImageSrc } from '../../../Blog/utils/getBlogImageSrc'
import { getHomepageFeaturedCaseStudies } from '../../utils/homepagePosts'
import HomeLink from '../HomeLink/HomeLink'
import styles from './styles.module.scss'

const FeaturedCaseStudySlide = ({
  post,
  ctaLabel,
}: {
  post: ReturnType<typeof getHomepageFeaturedCaseStudies>[number]
  ctaLabel: string
}) => {
  const { frontmatter } = post
  const image = getBlogImageSrc(frontmatter.homepageImage || frontmatter.image)
  const title = frontmatter.homepageTitle || frontmatter.title
  const highlights = frontmatter.homepageHighlights || []

  return (
    <>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.imageWrap}>
        {image && (
          <img
            src={image}
            alt={frontmatter.homepageImageAlt || frontmatter.imageAlt || title || ''}
          />
        )}
      </div>
      <div className={styles.caseNavigation}>
        <HomeLink
          className={styles.cta}
          to={`/blog/${post.slug}`}
          tracking={{
            event: 'homepage_featured_case_study_click',
            tagName: `Homepage featured case study: ${post.slug}`,
          }}
        >
          {ctaLabel} <span aria-hidden>→</span>
        </HomeLink>
      </div>
      {highlights.length ? (
        <div className={styles.highlights}>
          {highlights.map((highlight) => (
            <div key={highlight.label} className={styles.highlight}>
              <h3>{highlight.label}</h3>
              <p>{highlight.text}</p>
            </div>
          ))}
        </div>
      ) : null}
    </>
  )
}

type FeaturedCaseStudyProps = {
  eyebrow: string
  ctaLabel: string
}

const FeaturedCaseStudy = ({ eyebrow, ctaLabel }: FeaturedCaseStudyProps) => {
  const posts = getHomepageFeaturedCaseStudies()

  if (!posts.length) return null

  return (
    <section className={styles.section} aria-label="Featured case studies">
      <div className={styles.content}>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <Swiper
          className={styles.slider}
          modules={[A11y, Keyboard, Pagination]}
          slidesPerView={1}
          speed={500}
          keyboard={{ enabled: true, onlyInViewport: true }}
          pagination={{ clickable: true, bulletElement: 'button' }}
          a11y={{
            enabled: true,
            containerMessage: 'Featured case studies',
            itemRoleDescriptionMessage: 'case study',
            paginationBulletMessage: 'Go to case study {{index}}',
            slideLabelMessage: 'Case study {{index}} of {{slidesLength}}',
          }}
        >
          {posts.map((post) => (
            <SwiperSlide key={post.slug} tag="article" className={styles.slide}>
              <FeaturedCaseStudySlide post={post} ctaLabel={ctaLabel} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}

export default FeaturedCaseStudy
